<template>
  <div>
    <dx-modal
      :value="show"
      @input="close"
      :title="modalTitle"
      id="schedule-hour-config-modal"
      size="lg">
      <div class="loading loading-lg" v-if="loadingForm" />
      <template v-else>
      <ul class="step">
        <li
          v-for="(item, i) in steps"
          class="step-item"
          :class="[active(item.id), { visited: visited.includes(item.id) && item.id !== step }]"
          :key="i"
        ><a href="#" @click.prevent="goToStep(item.id)">
          {{ item.title }}
          <span class="step-count" v-if="stepCount(item.id)">{{ stepCount(item.id) }}</span>
        </a></li>
      </ul>


      <template v-if="step === 1">
        <div class="step-head">
          <span class="step-eyebrow">{{ stepLabel }}</span>
          <h5 class="step-title">Quando</h5>
          <p class="step-hint" v-if="duplicateId">
            Horários, profissionais, convênios, serviços e regras já vêm do horário original.
            Informe quando o novo horário acontece e ajuste o resto nos próximos passos.
          </p>
          <p class="step-hint" v-else>Escolha o tipo de horário e os dias em que ele acontece.</p>
        </div>
        <div class="duplicate-source" v-if="source">
          <span class="hero-icon"><fa-icon :icon="['fal', 'copy']"/></span>
          <span class="duplicate-source-text">
            <span class="duplicate-source-label">Cópia de</span>
            <strong>{{ source.when }}</strong>
            <span class="duplicate-source-ranges">{{ source.ranges }}</span>
          </span>
          <span class="duplicate-source-counts">
            <span class="tooltip" data-tooltip="Profissionais">
              <fa-icon :icon="['fal', 'user-md']"/>{{ source.professionals }}
            </span>
            <span class="tooltip" data-tooltip="Convênios">
              <fa-icon :icon="['fal', 'shield-check']"/>{{ source.insurances }}
            </span>
            <span class="tooltip tooltip-left" data-tooltip="Serviços">
              <fa-icon :icon="['fal', 'stethoscope']"/>{{ source.expenses }}
            </span>
          </span>
        </div>
        <template v-if="!form.id && !duplicateId">
          <div class="modal-schedule-card-items">
            <div
              class="modal-schedule-card-item"
              :class="{ 'is-active': form.type === 'week' }"
              @click="form.type = 'week'"
            >
              <div class="item-content">
                <fa-icon class="icon" :icon="['fal', 'calendar-alt']" />
                <strong>Horários recorrentes</strong>
                <small class="text-dark mt-2">
                  Utilize quando os horários forem se repetir
                  todas as semanas sem uma data de término
                </small>
              </div>
            </div>
            <div
              class="modal-schedule-card-item"
              :class="{ 'is-active': form.type === 'scale' }"
              @click="form.type = 'scale'"
            >
              <div class="item-content">
                <fa-icon class="icon" :icon="['fal', 'calendar-day']" />
                <strong>Horários de escala</strong>
                <small class="text-dark mt-2">
                  Utilize quando os horários forem de datas específicas
                </small>
              </div>
            </div>
          </div>
        </template>

        <template v-if="form.type">
          <div class="columns" v-if="!form.id">
            <div
              class="column col-4 col-md-4 col-sm-12 form-group"
              :class="{ 'has-error': $v.form.startDate.$error }"
              v-if="form.type === 'scale'"
            >
              <label class="form-label">Data início</label>
              <dx-input-date
                id="start-date"
                class="form-input text-center"
                v-model="form.startDate"
                @blur="$v.form.startDate.$touch()"
              />
              <template v-if="$v.form.startDate.$error">
                <div class="form-input-hint" v-if="!$v.form.startDate.required">
                  Campo obrigatório
                </div>
                <div class="form-input-hint" v-else-if="!$v.form.startDate.date">
                  Data inválida
                </div>
                <div class="form-input-hint" v-else-if="!$v.form.startDate.minDate">
                  Não pode ser inferior a data atual
                </div>
              </template>
            </div>

            <div
              class="column col-4 col-md-4 col-sm-12 form-group"
              :class="{ 'has-error': $v.form.endDate.$error }"
              v-if="form.type === 'scale'"
            >
              <label class="form-label">Data término</label>
              <dx-input-date
                id="end-date"
                class="form-input text-center"
                v-model="form.endDate"
                @blur="$v.form.endDate.$touch()"
              />
              <template v-if="$v.form.endDate.$error">
                <div class="form-input-hint" v-if="!$v.form.endDate.required">
                  Campo obrigatório
                </div>
                <div class="form-input-hint" v-else-if="!$v.form.endDate.date">
                  Data inválida
                </div>
                <div class="form-input-hint" v-else-if="!$v.form.endDate.minDate">
                  Não pode ser inferior a hoje
                </div>
                <div class="form-input-hint" v-else-if="!$v.form.endDate.maxDate">
                  Deve ser inferior a 6 meses
                </div>
              </template>
            </div>

            <div
              class="column col-4 form-group"
              :class="{ 'has-error': $v.form.repeat.$error }"
              v-if="form.type === 'scale'"
            >
              <label for="repeat" class="form-label">Repetir</label>
              <select
                id="repeat"
                class="form-select"
                v-model="form.repeat"
                @change="$v.form.repeat.$touch()"
              >
                <option :value="1">Toda semana</option>
                <option :value="2">a cada 2 semanas</option>
                <option :value="3">a cada 3 semanas</option>
                <option :value="4">a cada 4 semanas</option>
                <option :value="5">a cada 5 semanas</option>
                <option :value="6">a cada 6 semanas</option>
                <option :value="7">a cada 7 semanas</option>
                <option :value="8">a cada 8 semanas</option>
              </select>
              <div class="form-input-hint" v-if="$v.form.repeat.$error && !$v.form.repeat.required">
                Campo obrigatório
              </div>
            </div>

            <div
              class="column col-12 form-group"
              :class="{ 'has-error': $v.form.weekDays.$error }"
            >
              <label for="week-days" class="form-label">
                Escolha abaixo os dias da semana que estes horários irão se repetir
              </label>
              <div class="week-days" id="week-days">
                <label
                  class="form-checkbox form-inline"
                  v-for="(item, i) in weekDays"
                  :key="i"
                >
                  <input type="checkbox" v-model="item.selected">
                  <i class="form-icon" />{{ item.name }}
                </label>
              </div>
              <div class="form-input-hint" v-if="$v.form.weekDays.$error">
                Selecione ao menos um dia da semana
              </div>
            </div>
          </div>

        </template>
      </template>

      <template v-if="step === 2">
        <div class="step-head">
          <span class="step-eyebrow">{{ stepLabel }}</span>
          <h5 class="step-title">Horários</h5>
          <p class="step-hint">Defina como este horário atende e as faixas de atendimento.</p>
        </div>
        <div class="hours-panel">
          <div class="mode-row">
            <div class="mode-cards" v-if="!form.slot">
              <div
                class="mode-card"
                :class="{ 'is-active': rangeType === 'schedule' }"
                @click="setRangeType('schedule')"
              >
                <span class="mode-icon"><fa-icon :icon="['fal', 'clock']"/></span>
                <span class="mode-text">
                  <strong>Em horários agendados</strong>
                  <small>Uma vaga a cada intervalo de minutos</small>
                </span>
                <span class="mode-check" v-if="rangeType === 'schedule'">
                  <fa-icon :icon="['fal', 'check-circle']"/>
                </span>
              </div>
              <div
                class="mode-card"
                :class="{ 'is-active': rangeType === 'arrivalOrder' }"
                @click="setRangeType('arrivalOrder')"
              >
                <span class="mode-icon"><fa-icon :icon="['fal', 'users']"/></span>
                <span class="mode-text">
                  <strong>Por ordem de chegada</strong>
                  <small>Sem intervalo, atende conforme chega</small>
                </span>
                <span class="mode-check" v-if="rangeType === 'arrivalOrder'">
                  <fa-icon :icon="['fal', 'check-circle']"/>
                </span>
              </div>
            </div>
            <label class="form-switch slot-switch">
              <input
                type="checkbox"
                name="slot"
                v-model="form.slot"
                @change="onSlotChange(form.slot)"
              >
              <i class="form-icon" />Agenda exclusiva para encaixes
            </label>
          </div>

          <template v-if="!form.slot">
            <div class="ranges-head">Faixas de horários</div>

            <div
              class="range-row"
              :class="{ 'is-arrival': rangeType === 'arrivalOrder' }"
              v-for="(range, i) in form.ranges"
              :key="range.key"
            >
              <range
                :item="range"
                :arrivalOrder="rangeType === 'arrivalOrder'"
                :index="i"
                ref="timeRange"
                @removeRange="removeRange"
              >
                <div class="column col-auto form-group" v-if="rangeType === 'schedule'">
                  <span class="range-slots">
                    <strong>{{ slotsOf(range) }}</strong>
                    {{ slotsOf(range) === 1 ? 'vaga' : 'vagas' }}
                  </span>
                </div>
                <div class="column col-auto form-group" v-else-if="durationOf(range)">
                  <span class="range-slots"><strong>{{ durationOf(range) }}</strong></span>
                </div>
              </range>
            </div>

            <div
              class="ranges-message"
              :class="rangesMessage.type"
              v-if="rangesMessage && rangesMessage.type"
            >
              <fa-icon :icon="['fal', 'exclamation-triangle']"/>{{ rangesMessage.message }}
            </div>

            <div class="ranges-foot">
              <button
                class="btn btn-secondary btn-sm btn-icon btn-icon-left"
                v-if="rangeType === 'schedule'"
                @click="addRange"
              ><fa-icon :icon="['fal', 'plus']"/>Adicionar faixa de horário</button>
              <span v-else/>
              <span class="ranges-total" v-if="rangeType === 'schedule'">
                <strong>{{ totalSlots }}</strong> vagas por dia
              </span>
            </div>
          </template>
        </div>

        <div class="extra-rules" :class="{ 'is-open': showAdvancedRules }">
          <div class="extra-rules-header" @click="showAdvancedRules = !showAdvancedRules">
            <span class="extra-rules-title">
              <fa-icon :icon="['fal', 'cog']"/>
              <span class="extra-rules-text">
                <strong>Outros recursos</strong>
                <small>Faixa etária, limites de retorno e encaixe, vigência, agrupamento</small>
              </span>
            </span>
            <span class="extra-rules-right">
              <span class="status-pill is-set" v-if="advancedCount > 0">
                {{ advancedCount }} {{ advancedCount === 1 ? 'definido' : 'definidos' }}
              </span>
              <fa-icon
                class="extra-rules-icon"
                :icon="['fal', showAdvancedRules ? 'chevron-up' : 'chevron-down']"
              />
            </span>
          </div>

          <div class="extra-rules-body" v-show="showAdvancedRules">
            <div class="divider" />

            <div class="columns">
              <div class="column col-6 form-group">
                <label for="age-limit-min" class="form-label">Faixa etária (anos)</label>
                <div class="input-group">
                  <span class="input-group-addon">Min</span>
                  <input
                    id="age-limit-min"
                    type="number"
                    class="form-input"
                    v-model="form.ageRange.min"
                    min="0"
                    max="100"
                    placeholder="0"
                  >
                  <span class="input-group-addon">Max</span>
                  <input
                    id="age-limit-max"
                    type="number"
                    class="form-input"
                    v-model="form.ageRange.max"
                    min="0"
                    max="100"
                    placeholder="0"
                  >
                </div>
              </div>
              <div class="column col-3 form-group">
                <label for="return-daily-limit" class="form-label">
                  Limite de retorno
                  <span
                    class="text-small text-info tooltip"
                    data-tooltip="Limite diário de retorno"
                  >
                  <fa-icon :icon="['fal', 'info-circle']" />
                </span>
                </label>
                <input
                  id="return-daily-limit"
                  type="number"
                  class="form-input"
                  v-model="form.dailyReturnLimit"
                  min="0"
                  placeholder="0"
                >
              </div>
              <div class="column col-3 form-group">
                <label for="slot-daily-limit" class="form-label">
                  Limite de encaixe
                  <span
                    class="text-small text-info tooltip tooltip-top-right"
                    data-tooltip="Limite diário de encaixe"
                  >
                  <fa-icon :icon="['fal', 'info-circle']" />
                </span>
                </label>
                <input
                  id="slot-daily-limit"
                  type="number"
                  class="form-input"
                  v-model="form.dailySlotLimit"
                  min="0"
                  placeholder="0"
                >
              </div>
            </div>

            <div class="divider" />

            <div class="columns">
              <div
                class="column col-3 form-group"
                :class="{ 'has-error': $v.form.validity.from.$error }"
              >
                <label for="validity-from" class="form-label">Vigência inicial</label>
                <dx-input-date
                  id="validity-from"
                  class="form-input"
                  v-model="form.validity.from"
                />
                <st-form-input-hint :validator="$v.form.validity.from" />
              </div>
              <div
                class="column col-3 form-group"
                :class="{ 'has-error': $v.form.validity.to.$error }"
              >
                <label for="validity-to" class="form-label">Vigência final</label>
                <dx-input-date
                  id="validity-to"
                  class="form-input"
                  v-model="form.validity.to"
                />
                <st-form-input-hint :validator="$v.form.validity.to" />
              </div>
              <div class="column col-6">
                <label for="shared-slots" class="form-label">
                  Agrupar por horário?
                  <span
                    class="tooltip text-info ml-1"
                    data-tooltip="Exibe um único horário para vários profissionais"
                  >
                  <fa-icon :icon="['fal', 'info-circle']" />
                </span>
                </label>
                <select id="shared-slots" class="form-select" v-model="form.sharedSlots">
                  <option :value="true">Sim</option>
                  <option :value="false">Não</option>
                </select>
              </div>
            </div>

            <label class="form-label">
              Observações adicionais
              <textarea rows="2" class="form-input" v-model="form.notes" />
            </label>
          </div>
        </div>
      </template>

      <template v-if="step === 3">
        <div class="step-head">
          <span class="step-eyebrow">{{ stepLabel }}</span>
          <h5 class="step-title">Profissionais</h5>
          <p class="step-hint">
            Quem atende neste horário. Busque ou liste por especialidade, marque vários
            e adicione de uma vez. Se não existir, cadastre sem sair daqui.
          </p>
        </div>
        <div class="step-layout" :class="{ 'is-creating': !!creating }">
          <div class="step-main">
            <step-picker
              ref="professionalPicker"
              :items="professionalOptions"
              :selected-ids="selectedProfessionalKeys"
              :loading="loadingProfessionals"
              :creating="creating === 'professional'"
              :creatable="canCreateProfessional"
              :has-any="professionalOptions.length > 0 || loadingProfessionals"
              create-label="profissional"
              create-hint="Cria o profissional e o acesso ao sistema"
              placeholder="Buscar profissional por nome, conselho ou especialidade..."
              empty-title="Nenhum profissional cadastrado ainda"
              empty-hint="Cadastre quem vai atender. O acesso ao sistema é criado junto."
              none-label="Nenhum profissional encontrado"
              @add="addProfessionals"
              @create="startCreate('professional', $event)"
            >
              <template v-slot:create>
                <professional-form
                  v-if="creating === 'professional'"
                  :initial-name="createQuery"
                  @saved="onProfessionalCreated"
                  @cancel="creating = null"
                />
              </template>
            </step-picker>
          </div>

          <aside class="step-side">
            <div class="side-head">
              <span class="side-title">
                <fa-icon :icon="['fal', 'user-md']"/>Neste horário
              </span>
              <span class="side-count" v-if="professionals.length > 0">
                {{ professionals.length }}
                {{ professionals.length === 1 ? 'profissional' : 'profissionais' }}
              </span>
            </div>
            <ul class="side-list" v-if="professionals.length > 0">
              <li
                class="side-item"
                :class="{ 'is-new': p.isNew }"
                v-for="(p, i) in professionals"
                :key="`${p.id}:${p.specialty.code || ''}`"
              >
                <span class="side-text">
                  <span class="side-name" :title="p.name">{{ p.name }}</span>
                  <span class="side-meta" :title="p.specialty && p.specialty.name">
                    {{ p.specialty && p.specialty.name }}
                    <span class="label label-success" v-if="p.isNew">novo</span>
                  </span>
                </span>
                <button
                  class="btn btn-sm btn-action btn-icon btn-gray side-remove tooltip tooltip-left"
                  data-tooltip="Remover"
                  @click="removeProfessional(i)"
                ><fa-icon :icon="['fal', 'times']"/></button>
              </li>
            </ul>
            <div class="side-empty" v-else>
              <fa-icon :icon="['fal', 'user-plus']" size="2x"/>
              <strong>Nenhum profissional adicionado</strong>
              <span>Marque na lista ao lado e clique em Adicionar.</span>
            </div>
          </aside>
        </div>
      </template>

      <template v-if="step === 4">
        <div class="step-head">
          <span class="step-eyebrow">{{ stepLabel }}</span>
          <h5 class="step-title">Convênios</h5>
          <p class="step-hint">
            Operadoras e planos aceitos neste horário. Liste por operadora para adicionar
            todos os planos dela de uma vez. Um plano novo ganha a tabela de preços
            “Valores Operadora” automaticamente.
          </p>
        </div>
        <div class="step-layout" :class="{ 'is-creating': !!creating }">
          <div class="step-main">
            <div class="from-professional" v-if="singleProfessional && !creating">
              <label class="form-checkbox">
                <input
                  type="checkbox"
                  v-model="fromProfessional.insurances.checked"
                  :disabled="fromProfessional.insurances.loading"
                  @change="toggleProfessionalInsurances($event.target.checked)"
                >
                <i class="form-icon"></i>
                Adicionar todos os convênios vinculados a
                <strong>{{ singleProfessional.name }}</strong>
              </label>
              <span class="from-professional-status">
                {{ fromProfessionalStatus(fromProfessional.insurances, 'convênio') }}
              </span>
            </div>
            <step-picker
              ref="planPicker"
              :items="planOptions"
              :selected-ids="selectedPlanIds"
              :loading="loadingPlans"
              :creating="creating === 'plan'"
              :creatable="canCreateInsurance"
              :has-any="planOptions.length > 0 || loadingPlans"
              create-label="convênio"
              create-hint="Escolha a operadora ou cadastre uma nova"
              placeholder="Buscar operadora ou plano..."
              empty-title="Nenhum convênio cadastrado ainda"
              empty-hint="Cadastre ao menos o atendimento particular ou o primeiro convênio."
              none-label="Nenhum convênio encontrado"
              @add="addPlans"
              @create="startCreate('plan', $event)"
            >
              <template v-slot:create>
                <insurance-plan-form
                  v-if="creating === 'plan'"
                  :initial-query="createQuery"
                  @saved="onPlanCreated"
                  @cancel="creating = null"
                />
              </template>
            </step-picker>
          </div>

          <aside class="step-side">
            <div class="side-head">
              <span class="side-title">
                <fa-icon :icon="['fal', 'shield-check']"/>Neste horário
              </span>
              <span class="side-count" v-if="insurances.length > 0">
                {{ insurances.length }} {{ insurances.length === 1 ? 'convênio' : 'convênios' }}
              </span>
            </div>
            <ul class="side-list" v-if="insurances.length > 0">
              <li
                class="side-item has-limit"
                :class="{ 'is-new': ins.isNew }"
                v-for="(ins, i) in insurances"
                :key="ins.planId"
              >
                <span class="side-text">
                  <span class="side-name" :title="`${ins.insuranceName} · ${ins.planName}`">
                    {{ ins.insuranceName }}
                    <span class="side-plan">· {{ ins.planName }}</span>
                  </span>
                  <span
                    class="side-meta"
                    :class="{ 'text-warning': !ins.tableName }"
                    :title="insuranceTableLabel(ins)"
                  >
                    <template v-if="ins.tableName">Tabela: {{ ins.tableName }}</template>
                    <template v-else>Sem tabela de preços vinculada</template>
                    <span class="label label-success" v-if="ins.isNew">novo</span>
                  </span>
                </span>
                <label
                  class="side-limit tooltip tooltip-left"
                  data-tooltip="Limite por dia"
                >
                  <input
                    type="text"
                    class="form-input input-sm text-center"
                    v-model="ins.dailyLimit"
                    v-mask="'000'"
                    placeholder="∞"
                  >
                </label>
                <button
                  class="btn btn-sm btn-action btn-icon btn-gray side-remove tooltip tooltip-left"
                  data-tooltip="Remover"
                  @click="removeInsurance(i)"
                ><fa-icon :icon="['fal', 'times']"/></button>
              </li>
            </ul>
            <div class="side-empty" v-else>
              <fa-icon :icon="['fal', 'shield-check']" size="2x"/>
              <strong>Nenhum convênio adicionado</strong>
              <span>Marque na lista ao lado e clique em Adicionar.</span>
            </div>
          </aside>
        </div>
      </template>

      <template v-if="step === 5">
        <div class="step-head">
          <span class="step-eyebrow">{{ stepLabel }}</span>
          <h5 class="step-title">Serviços</h5>
          <p class="step-hint">
            O que é atendido neste horário. Busque, marque vários e adicione de uma vez.
            Se não existir, cadastre sem sair daqui.
          </p>
        </div>
        <div class="step-layout" :class="{ 'is-creating': !!creating }">
          <div class="step-main">
            <div class="from-professional" v-if="singleProfessional && !creating">
              <label class="form-checkbox">
                <input
                  type="checkbox"
                  v-model="fromProfessional.expenses.checked"
                  :disabled="fromProfessional.expenses.loading"
                  @change="toggleProfessionalExpenses($event.target.checked)"
                >
                <i class="form-icon"></i>
                Adicionar todos os serviços vinculados a
                <strong>{{ singleProfessional.name }}</strong>
              </label>
              <span class="from-professional-status">
                {{ fromProfessionalStatus(fromProfessional.expenses, 'serviço') }}
              </span>
            </div>
            <step-picker
              ref="expensePicker"
              :items="expenseOptions"
              :selected-ids="selectedExpenseIds"
              :loading="loadingExpenses"
              :creating="creating === 'expense'"
              :creatable="canCreateExpense"
              :has-any="hasAnyExpense"
              remote
              create-label="serviço"
              create-hint="Depois inclua nas tabelas de preços dos convênios que atendem"
              placeholder="Buscar serviço por nome..."
              empty-title="Nenhum serviço cadastrado ainda"
              empty-hint="Comece pela consulta principal. Exames e procedimentos entram depois."
              none-label="Nenhum serviço encontrado"
              @search="searchExpenses"
              @add="addExpenses"
              @create="startCreate('expense', $event)"
            >
              <template v-slot:create>
                <expense-form
                  v-if="creating === 'expense'"
                  :initial-name="createQuery"
                  :preferred-specialties="selectedSpecialtyCodes"
                  @saved="onExpenseCreated"
                  @cancel="creating = null"
                />
              </template>
            </step-picker>
          </div>

          <aside class="step-side">
            <div class="side-head">
              <span class="side-title">
                <fa-icon :icon="['fal', 'stethoscope']"/>Neste horário
              </span>
              <span class="side-count" v-if="expenses.length > 0">
                {{ expenses.length }} {{ expenses.length === 1 ? 'serviço' : 'serviços' }}
              </span>
            </div>
            <ul class="side-list" v-if="expenses.length > 0">
              <li
                class="side-item"
                :class="{ 'is-new': exp.isNew }"
                v-for="(exp, i) in expenses"
                :key="exp.id"
              >
                <span class="side-text">
                  <span class="side-name" :title="exp.name">{{ exp.name }}</span>
                  <span class="side-meta" v-if="exp.type || exp.isNew">
                    <template v-if="exp.type">{{ expenseTypeName(exp.type) }}</template>
                    <span class="label label-success" v-if="exp.isNew">novo</span>
                  </span>
                </span>
                <button
                  class="btn btn-sm btn-action btn-icon btn-gray side-remove tooltip tooltip-left"
                  data-tooltip="Remover"
                  @click="removeExpense(i)"
                ><fa-icon :icon="['fal', 'times']"/></button>
              </li>
            </ul>
            <div class="side-empty" v-else>
              <fa-icon :icon="['fal', 'stethoscope']" size="2x"/>
              <strong>Nenhum serviço adicionado</strong>
              <span>Marque na lista ao lado e clique em Adicionar.</span>
            </div>
          </aside>
        </div>
      </template>

      <template v-if="step === 6">
        <div class="step-head">
          <span class="step-eyebrow">{{ stepLabel }}</span>
          <h5 class="step-title">Confira antes de salvar</h5>
          <p class="step-hint">
            Clique em editar para voltar a qualquer passo. Nada é gravado até você confirmar.
          </p>
        </div>
        <div class="summary-hero">
          <div class="hero-when">
            <span class="hero-icon"><fa-icon :icon="['fal', 'calendar-alt']"/></span>
            <span class="hero-text">
              <strong>{{ summaryWhen }}</strong>
              <span class="hero-period" v-if="summaryPeriod">{{ summaryPeriod }}</span>
              <span class="hero-ranges">
                <template v-if="form.slot">Agenda exclusiva para encaixes</template>
                <template v-else-if="rangeType !== 'schedule'">
                  {{ rangesText }} · por ordem de chegada
                </template>
                <template v-else>{{ rangesText }}</template>
              </span>
            </span>
          </div>
          <div class="hero-stats">
            <div class="hero-stat" v-if="!form.slot && rangeType === 'schedule'">
              <strong>{{ totalSlots }}</strong>
              <span>vagas/dia</span>
            </div>
            <div class="hero-stat">
              <strong>{{ professionals.length }}</strong>
              <span>{{ professionals.length === 1 ? 'profissional' : 'profissionais' }}</span>
            </div>
            <div class="hero-stat">
              <strong>{{ insurances.length }}</strong>
              <span>{{ insurances.length === 1 ? 'convênio' : 'convênios' }}</span>
            </div>
            <div class="hero-stat">
              <strong>{{ expenses.length }}</strong>
              <span>{{ expenses.length === 1 ? 'serviço' : 'serviços' }}</span>
            </div>
          </div>
        </div>

        <div class="summary">
          <div class="summary-card">
            <div class="summary-head">
              <span class="summary-title">
                <fa-icon :icon="['fal', 'cog']"/>Configuração
              </span>
              <button
                class="btn btn-sm btn-gray-outline btn-icon btn-icon-left"
                @click="goToStep(2)"
              ><fa-icon :icon="['fal', 'pencil']"/>Editar</button>
            </div>
            <ul>
              <li v-if="form.slot"><span class="row-main">Agenda exclusiva para encaixes</span></li>
              <template v-else>
                <li v-for="(r, i) in form.ranges" :key="i">
                  <span class="row-main">{{ r.start }} – {{ r.end }}</span>
                  <span class="row-meta">{{ rangeLabel(r) }}</span>
                </li>
              </template>
              <li v-for="(item, i) in summaryExtras" :key="`x${i}`">
                <span class="row-meta">{{ item }}</span>
              </li>
            </ul>
          </div>
          <div class="summary-card">
            <div class="summary-head">
              <span class="summary-title">
                <fa-icon :icon="['fal', 'user-md']"/>Profissionais
              </span>
              <button
                class="btn btn-sm btn-gray-outline btn-icon btn-icon-left"
                @click="goToStep(3)"
              ><fa-icon :icon="['fal', 'pencil']"/>Editar</button>
            </div>
            <ul>
              <li v-for="(p, i) in professionals" :key="i">
                <span class="row-main">
                  {{ p.name }}
                  <span class="status-pill is-new" v-if="p.isNew">novo</span>
                </span>
                <span class="row-meta">{{ p.specialty && p.specialty.name }}</span>
              </li>
            </ul>
          </div>
          <div class="summary-card">
            <div class="summary-head">
              <span class="summary-title">
                <fa-icon :icon="['fal', 'shield-check']"/>Convênios
              </span>
              <button
                class="btn btn-sm btn-gray-outline btn-icon btn-icon-left"
                @click="goToStep(4)"
              ><fa-icon :icon="['fal', 'pencil']"/>Editar</button>
            </div>
            <ul>
              <li class="row-stacked" v-for="ins in insurances" :key="ins.planId">
                <span class="row-main">
                  {{ ins.insuranceName }}
                  <span class="row-plan">· {{ ins.planName }}</span>
                  <span class="row-limit" v-if="Number(ins.dailyLimit) > 0">
                    {{ ins.dailyLimit }}/dia
                  </span>
                </span>
                <span class="status-pill is-new" v-if="ins.isNew">
                  <fa-icon :icon="['fal', 'plus-circle']"/>Tabela nova
                </span>
                <span class="status-pill is-ok" v-else-if="ins.tableName">
                  <fa-icon :icon="['fal', 'check-circle']"/>{{ ins.tableName }}
                </span>
                <span class="status-pill is-warn" v-else>
                  <fa-icon :icon="['fal', 'exclamation-triangle']"/>Sem tabela
                </span>
              </li>
            </ul>
          </div>
          <div class="summary-card">
            <div class="summary-head">
              <span class="summary-title">
                <fa-icon :icon="['fal', 'stethoscope']"/>Serviços
              </span>
              <button
                class="btn btn-sm btn-gray-outline btn-icon btn-icon-left"
                @click="goToStep(5)"
              ><fa-icon :icon="['fal', 'pencil']"/>Editar</button>
            </div>
            <ul>
              <li v-for="exp in expenses" :key="exp.id">
                <span class="row-main">
                  {{ exp.name }}
                  <span class="status-pill is-new" v-if="exp.isNew">novo</span>
                </span>
                <span class="row-meta" v-if="exp.type">{{ expenseTypeName(exp.type) }}</span>
              </li>
            </ul>
          </div>
        </div>

      </template>
      </template><!-- end v-else loadingForm -->

      <template slot="footer">
        <template v-if="loadingForm">
          <button class="btn" @click="close">Sair</button>
        </template>
        <template v-else>
          <span class="wizard-progress" v-if="!readonly">
            Passo <strong>{{ currentStepIndex + 1 }}</strong> de {{ steps.length }}
          </span>
          <button
            class="btn btn-icon btn-gray-outline mr-1"
            :disabled="saving"
            v-if="!isFirstStep && !readonly"
            @click="previewStep"
          >
            <fa-icon class="icon mr-2" :icon="['fal', 'chevron-left']" />Voltar
          </button>
          <button
            class="btn btn-icon btn-primary mr-1"
            :disabled="saving"
            v-if="!isLastStep && !readonly"
            @click="nextStep"
          >{{ step === 5 ? 'Revisar' : 'Avançar' }}
            <fa-icon class="icon ml-2" :icon="['fal', 'chevron-right']" />
          </button>
          <button
            class="btn btn-primary btn-icon btn-icon-left mr-1"
            :disabled="saving"
            :class="{ loading: saving }"
            v-if="isLastStep && !readonly"
            @click="save"
          ><fa-icon :icon="['fal', 'save']"/>Salvar</button>
          <button class="btn" @click="askClose">Sair</button>
        </template>
      </template>
    </dx-modal>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import Range from '@/app/schedule/components/modals/Range.vue';
import moment from 'moment/moment';
import { date, maxDate, minDate } from '@/data/validators';
import { resetCache } from '@/helpers/api';
import { getName as expenseTypeName } from '@/data/expense-types';
import {
  CREATE_EXPENSE,
  CREATE_INSURANCE,
  CREATE_PROFESSIONAL,
} from '@/data/actions/modules/general';
import StepPicker from './schedule-hour/StepPicker.vue';
import ProfessionalForm from './schedule-hour/ProfessionalForm.vue';
import InsurancePlanForm from './schedule-hour/InsurancePlanForm.vue';
import ExpenseForm from './schedule-hour/ExpenseForm.vue';

const EXPENSE_AVATARS = { consultation: 'Co', exam: 'Ex' };

function initials(name) {
  return String(name || '')
    .replace(/^Dra?\.\s*/i, '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();
}

function professionalKey(id, specialty) {
  return `${id}:${specialty && specialty.code ? specialty.code : ''}`;
}

export default {
  components: {
    StepPicker,
    ProfessionalForm,
    InsurancePlanForm,
    ExpenseForm,
    Range,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    scheduleId: {
      type: String,
      default: null,
    },
    hourId: {
      type: String,
      default: null,
    },
    duplicateId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      source: null,
      saving: false,
      readonly: false,
      loadingForm: false,
      rangeSeq: 0,
      step: 1,
      visited: [1],
      showAdvancedRules: false,
      rangeType: 'schedule',
      weekDays: [
        { day: 0, name: 'Dom', selected: false },
        { day: 1, name: 'Seg', selected: false },
        { day: 2, name: 'Ter', selected: false },
        { day: 3, name: 'Qua', selected: false },
        { day: 4, name: 'Qui', selected: false },
        { day: 5, name: 'Sex', selected: false },
        { day: 6, name: 'Sáb', selected: false },
      ],
      professionals: [],
      insurances: [],
      expenses: [],
      professionalOptions: [],
      planOptions: [],
      expenseResults: [],
      hasAnyExpense: true,
      loadingProfessionals: false,
      loadingPlans: false,
      loadingExpenses: false,
      fromProfessional: {
        insurances: this.blankFromProfessional(),
        expenses: this.blankFromProfessional(),
      },
      creating: null,
      createQuery: '',
      form: this.blankForm(),
    };
  },
  watch: {
    singleProfessional(current, previous) {
      const currentId = current ? current.id : null;
      const previousId = previous ? previous.id : null;
      if (currentId === previousId) return;
      this.fromProfessional.insurances = this.blankFromProfessional();
      this.fromProfessional.expenses = this.blankFromProfessional();
    },
  },
  mounted() {
    if (this.hourId || this.duplicateId) {
      this.load();
    } else {
      this.addRange();
      this.step = this.steps[0].id;
    }
    this.loadProfessionalOptions();
    this.loadPlanOptions();
    this.searchExpenses({ query: '' });
  },
  computed: {
    canCreateProfessional() {
      return this.$can(CREATE_PROFESSIONAL);
    },
    canCreateInsurance() {
      return this.$can(CREATE_INSURANCE);
    },
    canCreateExpense() {
      return this.$can(CREATE_EXPENSE);
    },
    isArrivalOrder() {
      return this.rangeType === 'arrivalOrder';
    },

    steps() {
      const steps = [
        { id: 1, title: 'Quando' },
        { id: 2, title: 'Horários' },
        { id: 3, title: 'Profissionais' },
        { id: 4, title: 'Convênios' },
        { id: 5, title: 'Serviços' },
        { id: 6, title: 'Resumo' },
      ];
      return this.form.id ? steps.filter(({ id }) => id !== 1) : steps;
    },
    stepLabel() {
      return `Passo ${this.currentStepIndex + 1} de ${this.steps.length}`;
    },

    modalTitle() {
      if (this.duplicateId) {
        return 'Duplicar horário';
      }
      if (this.form.type === 'week' && this.form.day !== undefined && this.form.day !== null) {
        const dayName = moment().day(this.form.day).format('dddd');
        return `Configurações de horários - ${dayName}`;
      }
      if (this.form.type === 'scale' && this.form.day) {
        return `Configurações de horários - ${moment.utc(this.form.day).format('DD [de] MMMM [de] YYYY')}`;
      }
      return 'Configurações de horários';
    },

    isFirstStep() {
      return this.step === this.steps[0].id;
    },

    isLastStep() {
      return this.step === this.steps[this.steps.length - 1].id;
    },

    currentStepIndex() {
      return this.steps.findIndex(s => s.id === this.step);
    },

    rangesMessage() {
      const ranges = this.form.ranges.filter(({ start, end }) => (
        !!start && !!end && start.length === 5 && end.length === 5
      ));

      if (ranges.length > 1) {
        const hasDuplicates = ranges.some(range => (
          ranges.filter(({ start }) => start === range.start).length > 1
          || ranges.filter(({ end }) => end === range.end).length > 1
        ));
        if (hasDuplicates) {
          return { type: 'error', message: 'Erro: Horários de início ou término duplicados' };
        }

        const hasConflicts = ranges.some(range => (
          ranges.filter(({ start }) => start >= range.start && start <= range.end).length > 1
          || ranges.filter(({ end }) => end >= range.start && end <= range.end).length > 1
        ));
        if (hasConflicts) {
          return { type: 'warning', message: 'Aviso: Um ou mais períodos colidem' };
        }
      }

      return null;
    },

    selectedWeekDays() {
      return this.weekDays.filter(item => item.selected);
    },

    selectedProfessionalKeys() {
      return this.professionals.map(p => professionalKey(p.id, p.specialty));
    },
    selectedPlanIds() {
      return this.insurances.map(({ planId }) => planId);
    },
    selectedExpenseIds() {
      return this.expenses.map(({ id }) => id);
    },
    selectedSpecialtyCodes() {
      return [...new Set(this.professionals
        .map(p => (p.specialty ? p.specialty.code : null))
        .filter(Boolean))];
    },
    singleProfessional() {
      const ids = [...new Set(this.professionals.map(({ id }) => id))];
      if (ids.length !== 1) return null;
      return this.professionals.find(({ id }) => id === ids[0]);
    },

    expenseOptions() {
      return this.expenseResults
        .map(item => ({
          id: item.id,
          title: item.name,
          meta: [expenseTypeName(item.type), item.specialty && item.specialty.name]
            .filter(Boolean).join(' · '),
          avatar: EXPENSE_AVATARS[item.type] || 'Pr',
          square: true,
          search: item.name,
        }));
    },
    summaryDays() {
      const days = this.selectedWeekDays.map(({ name }) => name).join(', ');
      if (days) return days;
      if (this.form.day !== undefined && this.form.day !== null) {
        if (this.form.type === 'week') return moment().day(this.form.day).format('dddd');
        return moment.utc(this.form.day).format('DD/MM/YYYY');
      }
      return '';
    },
    summaryWhen() {
      if (this.form.type === 'scale' && this.form.startDate
        && this.form.startDate === this.form.endDate) {
        return `Escala · ${moment(this.form.startDate).format('DD/MM/YYYY, dddd')}`;
      }
      const days = this.summaryDays;
      if (this.form.type === 'week') return days ? `Toda semana · ${days}` : 'Toda semana';
      return days ? `Escala · ${days}` : 'Escala';
    },
    summaryPeriod() {
      if (this.form.type !== 'scale' || !this.form.startDate || !this.form.endDate) return '';
      if (this.form.startDate === this.form.endDate) return '';
      const from = moment(this.form.startDate).format('DD/MM/YYYY');
      const to = moment(this.form.endDate).format('DD/MM/YYYY');
      const repeat = this.form.repeat === 1 ? 'toda semana' : `a cada ${this.form.repeat} semanas`;
      return `${from} a ${to} · ${repeat}`;
    },
    totalSlots() {
      return this.form.ranges.reduce((total, range) => total + this.slotsOf(range), 0);
    },
    advancedCount() {
      const { ageRange, validity } = this.form;
      return [
        ageRange.min || ageRange.max,
        this.form.dailyReturnLimit,
        this.form.dailySlotLimit,
        validity.from || validity.to,
        this.form.sharedSlots,
        this.form.notes,
      ].filter(Boolean).length;
    },
    rangesText() {
      return this.form.ranges.map(r => `${r.start} – ${r.end}`).join(' · ');
    },
    summaryExtras() {
      const { ageRange, validity } = this.form;
      const items = [];
      if (ageRange.min || ageRange.max) {
        items.push(`Faixa etária de ${ageRange.min || 0} a ${ageRange.max || '∞'} anos`);
      }
      if (this.form.dailyReturnLimit) items.push(`Limite de retorno ${this.form.dailyReturnLimit}/dia`);
      if (this.form.dailySlotLimit) items.push(`Limite de encaixe ${this.form.dailySlotLimit}/dia`);
      const from = validity.from ? moment(validity.from).format('DD/MM/YYYY') : '—';
      const to = validity.to ? ` a ${moment(validity.to).format('DD/MM/YYYY')}` : ', sem fim';
      items.push(`Vigência ${from}${to}`);
      if (this.form.sharedSlots) items.push('Agrupado por horário');
      if (this.form.notes) items.push(`Obs.: ${this.form.notes}`);
      return items;
    },
  },
  validations() {
    const rules = {
      form: {
        type: { required },
        startDate: {},
        endDate: {},
        repeat: {},
        weekDays: {
          required: () => this.form.id || this.selectedWeekDays.length > 0,
        },
        validity: {
          from: { date, required },
          to: { date, minDate: minDate(this.form.validity.from) },
        },
      },
    };

    if (!this.form.id && this.form.type === 'scale') {
      rules.form.startDate = { date, required, minDate: minDate() };
      rules.form.endDate = {
        date,
        required,
        minDate: minDate(this.form.startDate),
        maxDate: maxDate(moment.utc(this.form.startDate).add(6, 'months')),
      };
      rules.form.repeat = { required };
    }

    rules.step1 = ['form.startDate', 'form.endDate', 'form.repeat', 'form.weekDays'];
    rules.step2 = ['form.validity'];

    return rules;
  },
  methods: {
    expenseTypeName,
    addRange() {
      this.rangeSeq += 1;
      this.form.ranges.push({
        key: `r${this.rangeSeq}`, start: '', end: '', interval: '',
      });
    },
    insuranceTableLabel(ins) {
      return ins.tableName ? `Tabela: ${ins.tableName}` : 'Sem tabela de preços vinculada';
    },
    onSlotChange(slot) {
      if (!slot && this.form.ranges.length === 0) {
        this.addRange();
      }
    },
    setRangeType(type) {
      if (this.rangeType === type) return;
      this.rangeType = type;
      this.form.ranges = [];
      this.addRange();
    },
    removeRange(i) {
      if (this.form.ranges.length > 1) {
        this.form.ranges.splice(i, 1);
      }
    },
    active(id) {
      return id === this.step ? 'active' : '';
    },
    stepCount(id) {
      if (id === 3) return this.professionals.length;
      if (id === 4) return this.insurances.length;
      if (id === 5) return this.expenses.length;
      return 0;
    },
    durationOf(range) {
      const { start, end } = range;
      if (!start || !end || start.length !== 5 || end.length !== 5) return '';
      const toMinutes = t => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
      const total = toMinutes(end) - toMinutes(start);
      if (total <= 0) return '';
      const hours = Math.floor(total / 60);
      const minutes = total % 60;
      if (!hours) return `${minutes}min`;
      return minutes ? `${hours}h${String(minutes).padStart(2, '0')}` : `${hours}h`;
    },
    slotsOf(range) {
      const { start, end, interval } = range;
      if (!start || !end || start.length !== 5 || end.length !== 5 || !(Number(interval) > 0)) {
        return 0;
      }
      const toMinutes = t => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
      return Math.max(0, Math.ceil((toMinutes(end) - toMinutes(start)) / Number(interval)));
    },
    rangeLabel(range) {
      if (this.rangeType !== 'schedule') return 'ordem de chegada';
      return `a cada ${range.interval} min · ${this.slotsOf(range)} vagas`;
    },
    validateStep(step) {
      if (step === 1) {
        if (!this.form.type) {
          this.$toast.show('Escolha o tipo de horário para continuar', { type: 'error' });
          return false;
        }
        this.$v.step1.$touch();
        if (this.$v.step1.$error) return false;
      }
      if (step === 2) {
        this.$v.step2.$touch();
        if (this.$v.step2.$error) return false;
        if (!this.form.slot && this.form.ranges.length === 0) {
          this.$toast.show('Adicione ao menos uma faixa de horário', { type: 'error' });
          return false;
        }
        if (
          !this.form.slot
          && this.$refs.timeRange
          && !this.$refs.timeRange.every(item => item.validate())
        ) {
          return false;
        }
        if (!this.form.slot && this.rangesMessage && this.rangesMessage.type === 'error') {
          this.$toast.show(this.rangesMessage.message, { type: 'error' });
          return false;
        }
      }
      if (step === 3 && this.professionals.length === 0) {
        this.$toast.show('Adicione ao menos um profissional para continuar', { type: 'error' });
        return false;
      }
      if (step === 4 && this.insurances.length === 0) {
        this.$toast.show('Adicione ao menos um convênio para continuar', { type: 'error' });
        return false;
      }
      if (step === 5 && this.expenses.length === 0) {
        this.$toast.show('Adicione ao menos um serviço para salvar', { type: 'error' });
        return false;
      }
      return true;
    },
    setStep(id) {
      this.step = id;
      this.creating = null;
      if (!this.visited.includes(id)) this.visited.push(id);
    },
    goToStep(id) {
      if (id === this.step) return;
      if (id < this.step) {
        this.setStep(id);
        return;
      }
      if (!this.visited.includes(id)) return;
      if (!this.validateStep(this.step)) return;
      this.setStep(id);
    },
    previewStep() {
      if (this.currentStepIndex > 0) {
        this.setStep(this.steps[this.currentStepIndex - 1].id);
      }
    },
    nextStep() {
      if (!this.validateStep(this.step)) return;
      if (!this.isLastStep) {
        this.setStep(this.steps[this.currentStepIndex + 1].id);
      }
    },
    askClose() {
      if (this.duplicateId) {
        if (this.form.startDate || this.selectedWeekDays.length > 0) {
          this.confirmClose();
        } else {
          this.close();
        }
        return;
      }
      const hasChanges = this.professionals.length > 0
        || this.insurances.length > 0
        || this.expenses.length > 0
        || this.form.ranges.some(({ start }) => !!start);
      if (!hasChanges || this.form.id) {
        this.close();
        return;
      }
      this.confirmClose();
    },
    confirmClose() {
      this.$dialog.show('', {
        html: '<div class="text-center"><h5>Atenção!</h5>'
          + '<div>Você tem alterações não salvas neste horário. Deseja sair mesmo assim?</div></div>',
        buttons: [
          { label: 'Continuar editando', classes: '' },
          {
            label: 'Sair sem salvar',
            classes: 'btn-error ml-2',
            click: (dialogClose) => {
              dialogClose();
              this.close();
            },
          },
        ],
      });
    },
    async save() {
      if (this.saving) return;

      const pending = this.steps
        .map(({ id }) => id)
        .filter(id => id !== 6)
        .find(id => !this.validateStep(id));
      if (pending) {
        this.setStep(pending);
        return;
      }

      const days = this.weekDays
        .filter(item => item.selected)
        .map(item => item.day);

      const scaleDays = this.form.type === 'week'
        ? null
        : this.generateDays({
          startDate: this.form.startDate,
          endDate: this.form.endDate,
          weekDays: days,
          repeat: this.form.repeat,
        });

      if (!this.form.id && scaleDays && scaleDays.length === 0) {
        this.$toast.show(
          'Nenhum dia do período cai nos dias da semana selecionados. Ajuste o período ou os dias.',
          { type: 'error', timeout: 6000 },
        );
        this.setStep(1);
        return;
      }

      this.saving = true;

      const data = {
        type: this.form.type,
        scheduleId: this.scheduleId,
        startDate: this.form.startDate,
        endDate: this.form.endDate,
        arrivalOrder: this.rangeType === 'arrivalOrder',
        sharedSlots: this.form.sharedSlots,
        ranges: this.form.slot ? [] : this.form.ranges,
        ageRange: this.form.ageRange,
        validity: this.form.validity,
        dailyReturnLimit: this.form.dailyReturnLimit,
        dailySlotLimit: this.form.dailySlotLimit,
        notes: this.form.notes,
        repeat: this.form.repeat,
        days: this.form.type === 'week' ? days : scaleDays,
        professionals: this.professionals.map(p => ({
          id: p.id,
          specialty: p.specialty,
        })),
        insurancePlans: this.insurances.map(ins => ({
          id: ins.planId,
          dailyLimit: ins.dailyLimit || null,
        })),
        expenses: this.expenses.map(e => e.id),
      };

      const request = this.form.id
        ? this.$httpX.put(`/schedule-config/${this.form.id}`, data)
        : this.$httpX.post('/schedule-config', data);

      await request
        .then((response) => {
          this.showSaveResult(response.data);
          resetCache();
          this.$emit('save');
          this.close();
        })
        .catch((e) => {
          if (e.code && e.code === 'ECONNABORTED') {
            // O servidor continua gravando depois que o cliente aborta. Fechar o
            // modal e recarregar a lista evita um reenvio que duplicaria horários.
            this.$toast.show(
              'A conexão demorou demais. Os horários podem ter sido salvos: '
              + 'confira a lista antes de cadastrar novamente.',
              { type: 'warning', timeout: 10000 },
            );
            resetCache();
            this.$emit('save');
            this.close();
          } else if (e.response && e.response.status === 409) {
            this.$toast.show(
              (e.response.data && e.response.data.message)
                || 'Outro salvamento desta agenda está em andamento. Aguarde e tente novamente.',
              { type: 'warning', timeout: 10000 },
            );
          } else {
            this.$toast.show('Erro ao salvar o horário', { type: 'error' });
          }
        })
        .finally(() => {
          this.saving = false;
        });
    },
    showSaveResult(data) {
      if (this.form.id) {
        this.$toast.show('Horário salvo com sucesso');
        return;
      }

      const created = data && typeof data.created === 'number' ? data.created : null;
      const skipped = data && typeof data.skipped === 'number' ? data.skipped : 0;

      if (created === 0 && skipped > 0) {
        const target = skipped === 1 ? 'para o dia selecionado' : 'para os dias selecionados';
        this.$toast.show(
          `Nenhum horário foi criado: já existe um horário idêntico ${target}.`,
          { type: 'warning', timeout: 10000 },
        );
        return;
      }

      const label = this.duplicateId ? 'Horário duplicado' : 'Horário salvo com sucesso';

      if (skipped > 0) {
        const ignored = skipped === 1 ? '1 dia foi ignorado' : `${skipped} dias foram ignorados`;
        this.$toast.show(
          `${label}. ${ignored} por já existir horário idêntico.`,
          { type: 'warning', timeout: 10000 },
        );
        return;
      }

      this.$toast.show(label);
    },
    generateDays(params) {
      const startDate = moment(params.startDate);
      const endDate = moment(params.endDate);
      const repeat = params.repeat || 1;
      const weekDays = params.weekDays || [];
      const ctrl = {};
      const days = [];

      while (startDate.isSameOrBefore(endDate)) {
        if (Object.keys(ctrl).length === 0) {
          [0, 1, 2, 3, 4, 5, 6].forEach((day) => {
            ctrl[day] = startDate.day() > day ? 1 : 0;
          });
        }
        if (weekDays.includes(startDate.day()) && ctrl[startDate.day()] % repeat === 0) {
          days.push(startDate.format('YYYY-MM-DD'));
        }
        ctrl[startDate.day()] += 1;
        startDate.add(1, 'day');
      }

      return days;
    },
    close() {
      this.$emit('close');
    },
    startCreate(kind, query) {
      this.creating = kind;
      this.createQuery = query || '';
    },

    /* ----- professionals ----- */
    async loadProfessionalOptions() {
      this.loadingProfessionals = true;
      await this.$httpX
        .get('/professionals', { params: { active: true, limit: 0 } })
        .then(({ data }) => {
          this.professionalOptions = data.items.flatMap((item) => {
            const councils = item.professional.councils || [];
            const council = councils.length > 0 ? councils[0] : null;
            const list = item.professional.specialties || [];
            const specialties = list.length > 0 ? list : [{}];
            return specialties.map(specialty => ({
              id: professionalKey(item.id, specialty),
              title: item.name,
              meta: [
                specialty.name,
                council ? `${council.name}-${council.state} ${council.record}` : null,
              ].filter(Boolean).join(' · '),
              avatar: initials(item.name),
              search: `${item.name} ${specialty.name || ''} ${council ? council.record : ''}`,
              source: {
                id: item.id,
                name: item.name,
                council: council || {},
                specialty: specialty.code ? { code: specialty.code, name: specialty.name } : {},
              },
            }));
          });
        })
        .catch(() => {})
        .finally(() => {
          this.loadingProfessionals = false;
        });
    },
    addProfessionals(keys) {
      keys.forEach((key) => {
        const option = this.professionalOptions.find(({ id }) => id === key);
        if (option && !this.selectedProfessionalKeys.includes(key)) {
          this.professionals.push({ ...option.source });
        }
      });
    },
    onProfessionalCreated(items) {
      items.forEach((item) => {
        const key = professionalKey(item.id, item.specialty);
        if (!this.selectedProfessionalKeys.includes(key)) {
          this.professionals.push(item);
        }
      });
      this.creating = null;
      this.loadProfessionalOptions();
    },
    removeProfessional(i) {
      this.professionals.splice(i, 1);
    },

    /* ----- insurance plans ----- */
    async loadPlanOptions() {
      this.loadingPlans = true;
      await this.$httpX
        .get('/insurance-plans', { params: { active: true, limit: 0 } })
        .then(({ data }) => {
          this.planOptions = data.items.map((item) => {
            const table = item.plan.table || {};
            const tableId = table.procedureId || table.examId || null;
            return {
              id: item.plan.id,
              title: `${item.name} · ${item.plan.name}`,
              meta: tableId ? 'Tabela de preços vinculada' : 'Sem tabela de preços vinculada',
              warning: !tableId,
              avatar: item.plan.type === 'particular' ? 'P' : initials(item.name),
              square: true,
              search: `${item.name} ${item.plan.name}`,
              source: {
                planId: item.plan.id,
                insuranceId: item.id,
                insuranceName: item.name,
                planName: item.plan.name,
                planType: item.plan.type,
                tableId,
                tableName: tableId ? 'vinculada' : null,
                dailyLimit: null,
                isNew: false,
              },
            };
          });
          this.insurances.forEach((ins) => {
            const option = this.planOptions.find(({ id }) => id === ins.planId);
            if (option && !ins.tableId) {
              ins.tableId = option.source.tableId;
              ins.tableName = option.source.tableName;
            }
          });
          this.fillTableNames();
        })
        .catch(() => {})
        .finally(() => {
          this.loadingPlans = false;
        });
    },
    async fillTableNames() {
      const tableIds = this.planOptions.map(({ source }) => source.tableId).filter(Boolean);
      const ids = [...new Set(tableIds)];
      if (ids.length === 0) return;
      await this.$httpX
        .get('/expense-tables', { params: { active: true, limit: 0 } })
        .then(({ data }) => {
          const byId = Object.fromEntries(data.items.map(({ id, name }) => [id, name]));
          this.planOptions.forEach((option) => {
            const name = byId[option.source.tableId];
            option.source.tableName = name || option.source.tableName;
            option.meta = name ? `Tabela: ${name}` : option.meta;
          });
          this.insurances.forEach((ins) => {
            if (ins.tableId && byId[ins.tableId]) ins.tableName = byId[ins.tableId];
          });
        })
        .catch(() => {});
    },
    addPlans(ids) {
      ids.forEach((planId) => {
        const option = this.planOptions.find(({ id }) => id === planId);
        if (option && !this.selectedPlanIds.includes(planId)) {
          this.insurances.push({ ...option.source });
        }
      });
    },
    onPlanCreated(item) {
      if (!this.selectedPlanIds.includes(item.planId)) {
        this.insurances.push(item);
      }
      this.creating = null;
      this.loadPlanOptions();
    },
    removeInsurance(i) {
      this.insurances.splice(i, 1);
    },
    async toggleProfessionalInsurances(checked) {
      const state = this.fromProfessional.insurances;
      if (!checked) {
        this.insurances = this.insurances
          .filter(({ planId }) => !state.addedIds.includes(planId));
        state.addedIds = [];
        state.total = null;
        return;
      }

      state.loading = true;
      await this.$httpX
        .get(`/professionals/${this.singleProfessional.id}/insurance-plans`, {
          params: { limit: 0 },
        })
        .then(({ data }) => {
          const before = this.selectedPlanIds;
          this.addPlans(data.items.map(({ insurance }) => insurance.plan.id));
          state.addedIds = this.selectedPlanIds.filter(id => !before.includes(id));
          state.total = data.items.length;
        })
        .catch(() => {
          state.checked = false;
          this.$toast.show('Erro ao carregar os convênios do profissional', { type: 'error' });
        })
        .finally(() => {
          state.loading = false;
        });
    },

    /* ----- expenses ----- */
    async searchExpenses({ query }) {
      this.loadingExpenses = true;
      const params = { active: true, limit: 100 };
      if (query) params.search = query;

      await this.$httpX
        .get('/expenses', { params })
        .then(({ data }) => {
          this.expenseResults = data.items.map(item => ({
            id: item.id,
            name: item.name,
            type: item.type,
            specialty: item.specialty || null,
          }));
          if (!query) {
            this.hasAnyExpense = data.items.length > 0;
          }
          this.fillExpenseTypes();
        })
        .catch(() => {})
        .finally(() => {
          this.loadingExpenses = false;
        });
    },
    addExpenses(ids) {
      ids.forEach((expenseId) => {
        const item = this.expenseResults.find(({ id }) => id === expenseId);
        if (item && !this.selectedExpenseIds.includes(expenseId)) {
          this.expenses.push({ ...item, isNew: false });
        }
      });
    },
    onExpenseCreated(item) {
      if (!this.selectedExpenseIds.includes(item.id)) {
        this.expenses.push(item);
      }
      this.creating = null;
      this.hasAnyExpense = true;
    },
    removeExpense(i) {
      this.expenses.splice(i, 1);
    },
    async toggleProfessionalExpenses(checked) {
      const state = this.fromProfessional.expenses;
      if (!checked) {
        this.expenses = this.expenses.filter(({ id }) => !state.addedIds.includes(id));
        state.addedIds = [];
        state.total = null;
        return;
      }

      state.loading = true;
      await Promise.all([
        this.$httpX.get(`/professionals/${this.singleProfessional.id}/expenses`, {
          params: { limit: 0 },
        }),
        this.$httpX.get('/expenses', { params: { active: true, limit: 0 } }),
      ])
        .then(([{ data: linked }, { data: active }]) => {
          const before = this.selectedExpenseIds;
          const activeById = new Map(active.items.map(item => [item.id, item]));
          const items = linked.items
            .map(({ expense }) => activeById.get(expense.id))
            .filter(Boolean);
          items.forEach((expense) => {
            if (this.selectedExpenseIds.includes(expense.id)) return;
            this.expenses.push({
              id: expense.id,
              name: expense.name,
              type: expense.type || null,
              specialty: expense.specialty || null,
              isNew: false,
            });
          });
          state.addedIds = this.selectedExpenseIds.filter(id => !before.includes(id));
          state.total = items.length;
        })
        .catch(() => {
          state.checked = false;
          this.$toast.show('Erro ao carregar os serviços do profissional', { type: 'error' });
        })
        .finally(() => {
          state.loading = false;
        });
    },
    fromProfessionalStatus(state, label) {
      if (state.loading) return 'Carregando...';
      if (!state.checked || state.total === null) return '';
      if (state.total === 0) return `Nenhum ${label} vinculado ao profissional`;
      if (state.addedIds.length === 0) return 'Todos já estão neste horário';
      const count = state.addedIds.length;
      return `${count} ${count === 1 ? `${label} adicionado` : `${label}s adicionados`}`;
    },
    fillExpenseTypes() {
      this.expenses.forEach((exp, i) => {
        if (exp.type) return;
        const found = this.expenseResults.find(({ id }) => id === exp.id);
        if (found && found.type) {
          this.$set(this.expenses, i, { ...exp, type: found.type });
        }
      });
    },

    async load() {
      this.loadingForm = true;
      this.form.id = this.hourId || '';
      this.form.scheduleId = this.scheduleId;

      await this.$httpX
        .get(`/schedule-config/${this.hourId || this.duplicateId}`)
        .then(({ data }) => {
          this.rangeType = data.arrivalOrder ? 'arrivalOrder' : 'schedule';

          const ranges = (data.ranges || []).map((item) => {
            this.rangeSeq += 1;
            return { ...item, key: `r${this.rangeSeq}` };
          });
          this.form = {
            ...this.blankForm(),
            ...data,
            id: this.hourId || '',
            scheduleId: this.scheduleId,
            slot: ranges.length === 0,
            ranges,
          };

          if (this.duplicateId) {
            this.setDuplicateSource(data);
          }

          const days = data.days || [];
          this.weekDays = this.weekDays.map(wd => ({
            ...wd,
            selected: days.includes(wd.day),
          }));

          this.professionals = (data.professionals || []).map(p => ({
            id: p.id,
            name: p.name,
            specialty: p.specialty || {},
          }));

          this.insurances = (data.insurancePlans || []).map(ins => ({
            planId: ins.plan ? ins.plan.id : ins.id,
            insuranceName: ins.customName || ins.name || '',
            planName: ins.plan ? ins.plan.name : '',
            planType: ins.plan ? ins.plan.type : '',
            dailyLimit: ins.dailyLimit || null,
            tableId: null,
            tableName: null,
            isNew: false,
          }));

          this.expenses = (data.expenses || []).map(e => ({
            id: e.id,
            name: e.name,
            type: e.type || null,
          }));
          this.fillExpenseTypes();

          this.visited = this.steps.map(({ id }) => id);
          if (this.step === 1) this.step = 2;
        })
        .catch(() => {
          this.$toast.show('Erro ao carregar o horário', { type: 'error' });
          if (this.duplicateId) this.close();
        })
        .finally(() => {
          this.loadingForm = false;
          this.step = this.steps[0].id;
        });
    },
    setDuplicateSource(data) {
      const ranges = data.ranges || [];
      let when = 'Toda semana';
      if (data.type === 'scale') {
        when = `Escala · ${moment.utc(data.day).format('DD/MM/YYYY, dddd')}`;
      } else if (data.day !== undefined && data.day !== null) {
        when = `Toda semana · ${moment().day(data.day).format('dddd')}`;
      }

      this.source = {
        when,
        ranges: ranges.length === 0
          ? 'Agenda exclusiva para encaixes'
          : [
            ranges.map(r => `${r.start} – ${r.end}`).join(' · '),
            data.arrivalOrder ? 'por ordem de chegada' : '',
          ].filter(Boolean).join(' · '),
        professionals: (data.professionals || []).length,
        insurances: (data.insurancePlans || []).length,
        expenses: (data.expenses || []).length,
      };

      const today = moment.utc().format('YYYY-MM-DD');
      const validity = data.validity || {};
      this.form.day = null;
      this.form.startDate = '';
      this.form.endDate = '';
      this.form.repeat = 1;
      this.form.validity = {
        from: validity.from || today,
        to: validity.to && validity.to >= today ? validity.to : null,
      };
    },
    blankFromProfessional() {
      return {
        checked: false,
        loading: false,
        total: null,
        addedIds: [],
      };
    },
    blankForm() {
      return {
        id: '',
        type: '',
        startDate: '',
        endDate: '',
        arrivalOrder: false,
        sharedSlots: false,
        slot: false,
        ranges: [],
        notes: '',
        repeat: 1,
        ageRange: {
          min: undefined,
          max: undefined,
        },
        dailyReturnLimit: undefined,
        dailySlotLimit: undefined,
        validity: {
          from: moment.utc().format('YYYY-MM-DD'),
          to: null,
        },
        insurancePlans: [],
        professionals: [],
        expenses: [],
      };
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/scss/_variables.scss';

#schedule-hour-config-modal {
  .step {
    border-bottom: $border-width solid $border-color;
    counter-reset: wizard-step;
    margin-bottom: $layout-spacing-xl;
    padding-bottom: $layout-spacing-lg;
    .step-item {
      &:not(:first-child)::before {
        background: lighten($primary-color, 34%);
        height: 1px;
        top: .45rem;
      }
      a {
        color: $gray-color-dark;
        font-size: $font-size-sm;
        font-weight: 500;
        padding-top: 1.35rem;
        &:focus {
          box-shadow: none;
          outline: 0;
        }
        &::before {
          align-items: center;
          background: $light-color;
          border: 1px solid $border-color-dark;
          border-radius: 50%;
          color: $gray-color-dark;
          content: counter(wizard-step);
          counter-increment: wizard-step;
          display: flex;
          font-size: .5rem;
          font-weight: 600;
          height: .9rem;
          justify-content: center;
          line-height: 1;
          top: 0;
          width: .9rem;
        }
      }
      &.visited a {
        color: $gray-color-dark;
        &::before {
          background: lighten($primary-color, 33%);
          border-color: lighten($primary-color, 30%);
          color: $primary-color;
          content: '\2713';
        }
        &:hover {
          color: $primary-color;
        }
      }
      &.active a {
        color: $secondary-color-dark;
        font-weight: 600;
        &::before {
          background: $light-color;
          border-color: $primary-color;
          color: $primary-color;
        }
      }
      &.active ~ .step-item {
        &::before {
          background: $border-color;
        }
        a {
          color: $gray-color;
          &::before {
            background: $light-color;
            border-color: $border-color-dark;
            color: $gray-color;
            content: counter(wizard-step);
          }
        }
      }
    }
  }

  .modal-schedule-card-items {
    display: flex;
    margin-bottom: $layout-spacing-lg;
  }

  .modal-schedule-card-item {
    background-color: $light-color;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    height: 6rem;
    margin: $layout-spacing $layout-spacing-sm 0 $layout-spacing-sm;
    text-align: center;
    width: 50%;

    .item-content {
      background-color: $gray-color-ultra-light;
      border: $border-color-dark solid $border-width;
      border-radius: $border-radius;
      transition: border-color .15s ease, background-color .15s ease;
      display: flex;
      flex-direction: column;
      justify-content: center;
      height: 100%;
      padding: $layout-spacing;
    }

    .icon {
      color: $secondary-color;
      height: 1.2rem;
      margin-bottom: .4rem;
      width: auto;
    }
    strong {
      color: $gray-color-900;
    }

    &.is-active .item-content {
      border-color: $primary-color;
      background-color: lighten($primary-color, 36%);
    }
    &.is-active .icon {
      color: $primary-color;
    }

    &:hover .item-content {
      border-color: $primary-color;
    }
  }

  .week-days {
    border: $border-width solid $border-color-dark;
    border-radius: $border-radius;
    display: flex;
    justify-content: space-around;
  }

  .step-item {
    a {
      cursor: default;
      text-decoration: none;
    }
    &.visited a {
      cursor: pointer;
    }
    .step-count {
      background-color: lighten($primary-color, 36%);
      border: 1px solid lighten($primary-color, 26%);
      border-radius: 1rem;
      color: $primary-color-dark;
      display: inline-block;
      font-size: .5rem;
      font-weight: 700;
      line-height: 1;
      margin-left: $layout-spacing-sm;
      min-width: .9rem;
      padding: .14rem .3rem;
      text-align: center;
      vertical-align: middle;
    }
  }

  .step-head {
    margin-bottom: $layout-spacing-lg;
  }
  .step-eyebrow {
    color: $primary-color;
    display: block;
    font-size: .5rem;
    font-weight: 600;
    letter-spacing: .06em;
    text-transform: uppercase;
  }
  .step-title {
    color: $secondary-color-dark;
    font-size: .9rem;
    font-weight: 600;
    margin: $layout-spacing-sm 0 0;
  }
  .step-hint {
    color: $gray-color-dark;
    font-size: $font-size-sm;
    margin: $layout-spacing-sm 0 0;
    max-width: 44rem;
  }

  .step-layout {
    align-items: start;
    display: grid;
    gap: $layout-spacing-lg;
    grid-template-columns: minmax(0, 11fr) minmax(0, 9fr);
    &.is-creating {
      grid-template-columns: 1fr;
      .step-side {
        display: none;
      }
    }
  }

  .from-professional {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: $layout-spacing-sm $layout-spacing;
    margin-bottom: $layout-spacing;
    .form-checkbox {
      color: $gray-color-dark;
      font-size: $font-size-sm;
      margin: 0;
      strong {
        color: $gray-color-900;
      }
    }
    .from-professional-status {
      color: $gray-color-dark;
      font-size: $font-size-xs;
    }
  }

  .step-side {
    background-color: #F2FAF5;
    border: $border-width solid #BFE3CD;
    border-left: 4px solid #2FA36B;
    border-radius: $border-radius;
    display: flex;
    flex-direction: column;
    max-height: 22rem;
    min-height: 12rem;
  }
  .side-head {
    align-items: center;
    border-bottom: $border-width solid #BFE3CD;
    display: flex;
    gap: $layout-spacing;
    justify-content: space-between;
    padding: $layout-spacing $layout-spacing-lg;
  }
  .side-title {
    align-items: center;
    color: #1F7A4E;
    display: inline-flex;
    font-size: $font-size-sm;
    font-weight: 700;
    gap: $layout-spacing-sm;
    letter-spacing: .02em;
    text-transform: uppercase;
    svg {
      color: #2FA36B;
    }
  }
  .side-count {
    background-color: $light-color;
    border: $border-width solid #BFE3CD;
    border-radius: 1rem;
    color: #1F7A4E;
    font-size: $font-size-xs;
    font-weight: 600;
    line-height: 1;
    padding: .18rem .5rem;
    white-space: nowrap;
  }
  .side-list {
    list-style: none;
    margin: 0;
    overflow: auto;
    padding: 0;
  }
  .side-item {
    align-items: center;
    background-color: $light-color;
    border-bottom: $border-width solid #E3F4EA;
    display: grid;
    gap: $layout-spacing;
    grid-template-columns: minmax(0, 1fr) auto;
    margin: 0;
    padding: $layout-spacing-sm $layout-spacing-lg;
    &:last-child {
      border-bottom: 0;
    }
    &.is-new {
      background-color: #E3F4EA;
    }
    .side-remove {
      opacity: .6;
    }
    &:hover {
      .side-remove {
        opacity: 1;
      }
      .side-name,
      .side-meta {
        overflow: visible;
        text-overflow: clip;
        white-space: normal;
      }
    }
  }
  .side-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .side-name {
    color: $gray-color-900;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    .label {
      margin-left: $layout-spacing-sm;
    }
  }
  .side-plan {
    color: $secondary-color;
    font-weight: 500;
  }
  .side-meta {
    color: $gray-color-dark;
    font-size: $font-size-xs;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    .label {
      font-size: .5rem;
      line-height: 1;
      margin-left: $layout-spacing-sm;
      padding: .12rem .3rem;
      vertical-align: middle;
    }
  }
  .side-limit {
    align-items: center;
    display: flex;
    flex-direction: column;
    margin: 0;
    .form-input {
      width: 3rem;
    }
  }
  .side-item.has-limit {
    grid-template-columns: minmax(0, 1fr) auto auto;
  }
  .side-empty {
    align-items: center;
    color: $gray-color-dark;
    display: flex;
    flex: 1;
    flex-direction: column;
    font-size: $font-size-xs;
    gap: $layout-spacing-sm;
    justify-content: center;
    padding: $layout-spacing-xl $layout-spacing-lg;
    text-align: center;
    svg {
      color: #2FA36B;
      margin-bottom: $layout-spacing-sm;
    }
    strong {
      color: #1F7A4E;
      font-size: $font-size-sm;
    }
  }

  .wizard-progress {
    color: $gray-color-dark;
    float: left;
    font-size: $font-size-xs;
    line-height: 1.8rem;
    strong {
      color: $primary-color;
    }
  }

  .inline-form {
    background-color: #FFF8E6;
    border: $border-width solid #F2C94C;
    border-radius: $border-radius;
    margin-top: $layout-spacing;
    padding: $layout-spacing-lg;
    .inline-form-title {
      color: #8A5A00;
      font-weight: 700;
      margin-bottom: $layout-spacing;
    }
    .inline-chips {
      display: flex;
      flex-wrap: wrap;
      gap: $layout-spacing-sm;
      margin-top: $layout-spacing-sm;
      .chip {
        background-color: $light-color;
        border: $border-width solid $border-color-dark;
        color: $gray-color-900;
        margin: 0;
      }
    }
    .inline-note {
      align-items: flex-start;
      background-color: lighten($info-color, 48%);
      border: $border-width solid lighten($info-color, 34%);
      border-radius: $border-radius;
      color: $secondary-color-dark;
      display: flex;
      font-size: $font-size-xs;
      gap: $layout-spacing;
      margin-top: $layout-spacing-lg;
      padding: $layout-spacing $layout-spacing-lg;
      svg {
        flex-shrink: 0;
        margin-top: .1rem;
      }
    }
    .inline-form-actions {
      display: flex;
      gap: $layout-spacing;
      justify-content: flex-end;
      margin-top: $layout-spacing-lg;
    }
  }

  .duplicate-source {
    align-items: center;
    background-color: $gray-color-ultra-light;
    border: $border-width solid $border-color;
    border-radius: $border-radius;
    display: flex;
    gap: $layout-spacing;
    margin-bottom: $layout-spacing-lg;
    padding: $layout-spacing $layout-spacing-lg;
    .hero-icon {
      font-size: .8rem;
      height: 1.8rem;
      width: 1.8rem;
    }
  }
  .duplicate-source-text {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
    strong {
      color: $gray-color-900;
    }
  }
  .duplicate-source-label {
    color: $primary-color;
    font-size: .5rem;
    font-weight: 600;
    letter-spacing: .06em;
    text-transform: uppercase;
  }
  .duplicate-source-ranges {
    color: $gray-color-dark;
    font-size: $font-size-xs;
    font-variant-numeric: tabular-nums;
  }
  .duplicate-source-counts {
    display: flex;
    flex-shrink: 0;
    font-size: $font-size-xs;
    gap: $layout-spacing-sm;
    span {
      align-items: center;
      background-color: $light-color;
      border: $border-width solid $border-color;
      border-radius: 1rem;
      color: $gray-color-900;
      display: inline-flex;
      font-variant-numeric: tabular-nums;
      font-weight: 600;
      gap: .25rem;
      padding: .15rem .45rem;
      svg {
        color: $secondary-color;
      }
    }
  }

  .summary-hero {
    align-items: center;
    background-color: $gray-color-ultra-light;
    border: $border-width solid $border-color;
    border-radius: $border-radius;
    display: flex;
    gap: $layout-spacing-lg;
    justify-content: space-between;
    margin-bottom: $layout-spacing-lg;
    padding: $layout-spacing-lg;
  }
  .hero-when {
    align-items: center;
    display: flex;
    gap: $layout-spacing;
    min-width: 0;
  }
  .hero-icon {
    align-items: center;
    background-color: $light-color;
    border-radius: 50%;
    box-shadow: 0 2px 6px rgba($primary-color, .2);
    color: $primary-color;
    display: flex;
    flex-shrink: 0;
    font-size: 1rem;
    height: 2.2rem;
    justify-content: center;
    width: 2.2rem;
  }
  .hero-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
    strong {
      color: $gray-color-900;
      font-size: $font-size-lg;
    }
    .hero-period {
      color: $gray-color-dark;
      font-size: $font-size-xs;
      font-variant-numeric: tabular-nums;
    }
    .hero-ranges {
      color: $secondary-color-dark;
      font-size: $font-size-sm;
      font-variant-numeric: tabular-nums;
    }
  }
  .hero-stats {
    display: flex;
    flex-shrink: 0;
    gap: $layout-spacing-sm;
  }
  .hero-stat {
    background-color: $light-color;
    border: $border-width solid $border-color-dark;
    border-radius: $border-radius;
    display: flex;
    flex-direction: column;
    min-width: 4.2rem;
    padding: $layout-spacing-sm $layout-spacing;
    text-align: center;
    strong {
      color: $primary-color;
      font-size: 1.1rem;
      font-variant-numeric: tabular-nums;
      line-height: 1.1;
    }
    span {
      color: $gray-color-dark;
      font-size: $font-size-xs;
    }
  }

  .summary {
    align-items: start;
    display: grid;
    gap: $layout-spacing-lg;
    grid-template-columns: 1fr 1fr;
  }
  .summary-card {
    border: $border-width solid $border-color;
    border-radius: $border-radius;
    padding: $layout-spacing $layout-spacing-lg $layout-spacing-sm;
    ul {
      list-style: none;
      margin: 0;
      padding: 0;
    }
    li {
      align-items: center;
      border-top: $border-width solid $border-color;
      display: flex;
      gap: $layout-spacing;
      justify-content: space-between;
      margin: 0;
      padding: $layout-spacing-sm 0;
    }
    .row-main {
      color: $gray-color-900;
      font-size: $font-size-sm;
      font-weight: 500;
      min-width: 0;
    }
    .row-plan {
      color: $secondary-color;
      font-weight: 400;
    }
    .row-limit {
      background-color: $gray-color-ultra-light;
      border: $border-width solid $border-color-dark;
      border-radius: 1rem;
      color: $gray-color-dark;
      font-size: $font-size-xs;
      font-weight: 600;
      margin-left: $layout-spacing-sm;
      padding: 0 .35rem;
      white-space: nowrap;
    }
    .row-meta {
      color: $gray-color-dark;
      font-size: $font-size-xs;
      text-align: right;
      white-space: nowrap;
    }
    .row-stacked {
      align-items: flex-start;
      flex-direction: column;
      gap: 0;
      .status-pill {
        background-color: transparent;
        border: 0;
        color: $gray-color-dark;
        font-size: .5rem;
        font-weight: 400;
        line-height: 1.3;
        margin-left: 0;
        max-width: 100%;
        padding: 0;
        white-space: normal;
        svg {
          display: none;
        }
        &.is-warn {
          color: #8A5A00;
        }
      }
    }
  }
  .summary-head {
    align-items: center;
    display: flex;
    justify-content: space-between;
    padding-bottom: $layout-spacing-sm;
  }
  .summary-title {
    align-items: center;
    color: $gray-color-900;
    display: inline-flex;
    font-weight: 700;
    gap: $layout-spacing-sm;
    svg {
      color: $primary-color;
    }
  }

  .status-pill {
    align-items: center;
    background-color: $light-color;
    border: $border-width solid;
    border-radius: 1rem;
    display: inline-flex;
    font-size: $font-size-xs;
    font-weight: 600;
    gap: .25rem;
    line-height: 1;
    margin-left: $layout-spacing-sm;
    padding: .18rem .45rem;
    white-space: nowrap;
    &.is-ok {
      border-color: #BFE3CD;
      color: #1F7A4E;
    }
    &.is-warn {
      border-color: #F2C94C;
      color: #8A5A00;
    }
    &.is-new {
      background-color: #F2FAF5;
      border-color: #BFE3CD;
      color: #1F7A4E;
    }
    &.is-set {
      background-color: lighten($primary-color, 36%);
      border-color: lighten($primary-color, 26%);
      color: $primary-color-dark;
    }
  }


  @media screen and (max-width: $size-md) {
    .step-layout {
      grid-template-columns: 1fr;
    }
    .step-side {
      max-height: none;
      min-height: 0;
    }
  }

  @media screen and (max-width: $size-sm) {
    .summary {
      grid-template-columns: 1fr;
    }
    .summary-hero {
      align-items: stretch;
      flex-direction: column;
    }
    .mode-cards {
      flex-direction: column;
    }
    .mode-card {
      max-width: none;
    }
    .hero-stats {
      flex-wrap: wrap;
    }
    .duplicate-source {
      flex-wrap: wrap;
    }
    .wizard-progress {
      display: none;
    }
  }

  .hours-panel {
    border: $border-width solid $border-color;
    border-radius: $border-radius;
    padding: $layout-spacing-lg;
  }
  .mode-row {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: $layout-spacing-lg;
    justify-content: space-between;
  }
  .mode-cards {
    display: flex;
    flex: 1;
    gap: $layout-spacing;
    min-width: 0;
  }
  .mode-card {
    align-items: center;
    background-color: $gray-color-ultra-light;
    border: $border-width solid $border-color-dark;
    border-radius: $border-radius;
    cursor: pointer;
    display: flex;
    flex: 1;
    gap: $layout-spacing;
    max-width: 16rem;
    padding: $layout-spacing $layout-spacing-lg;
    transition: border-color .15s ease, background-color .15s ease;
    &:hover {
      border-color: $primary-color;
    }
    &.is-active {
      background-color: lighten($primary-color, 36%);
      border-color: $primary-color;
      .mode-icon {
        background-color: $primary-color;
        color: $light-color;
      }
    }
    .mode-icon {
      align-items: center;
      background-color: lighten($primary-color, 33%);
      border-radius: 50%;
      color: $primary-color;
      display: flex;
      flex-shrink: 0;
      height: 1.6rem;
      justify-content: center;
      width: 1.6rem;
    }
    .mode-text {
      display: flex;
      flex-direction: column;
      min-width: 0;
      strong {
        color: $gray-color-900;
      }
      small {
        color: $gray-color-dark;
        font-size: $font-size-xs;
      }
    }
    .mode-check {
      color: $primary-color;
      margin-left: auto;
    }
  }
  .slot-switch {
    color: $gray-color-dark;
    margin: 0;
    white-space: nowrap;
  }
  .ranges-head {
    border-top: $border-width solid $border-color;
    color: $secondary-color-dark;
    font-size: $font-size-xs;
    font-weight: 700;
    letter-spacing: .04em;
    margin-top: $layout-spacing-lg;
    padding-top: $layout-spacing-lg;
    text-transform: uppercase;
  }
  .range-row {
    &.is-arrival .range .column.auto {
      flex: 0 0 auto;
      .form-input {
        width: 5rem;
      }
    }
    &.is-arrival .range .column.col-auto:last-child {
      margin-left: auto;
    }
    .range-slots {
      background-color: $light-color;
      border: $border-width solid $border-color-dark;
      border-radius: 1rem;
      color: $gray-color-dark;
      display: inline-block;
      font-size: $font-size-xs;
      font-variant-numeric: tabular-nums;
      line-height: 1;
      margin: 0 $layout-spacing-sm;
      padding: .3rem .5rem;
      white-space: nowrap;
      strong {
        color: $gray-color-900;
        font-size: $font-size-sm;
      }
    }
  }
  .ranges-message {
    align-items: center;
    border-radius: $border-radius;
    display: flex;
    font-size: $font-size-sm;
    gap: $layout-spacing-sm;
    margin-top: $layout-spacing-sm;
    padding: $layout-spacing-sm $layout-spacing;
    &.error {
      background-color: lighten($error-color, 42%);
      color: darken($error-color, 10%);
    }
    &.warning {
      background-color: #FFF8E6;
      color: #8A5A00;
    }
  }
  .ranges-foot {
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin-top: $layout-spacing;
    .ranges-total {
      color: $gray-color-dark;
      font-size: $font-size-sm;
      strong {
        color: $gray-color-900;
        font-size: $font-size-lg;
        font-variant-numeric: tabular-nums;
      }
    }
  }

  .extra-rules {
    border: $border-width solid $border-color;
    border-radius: $border-radius;
    margin-top: $layout-spacing-lg;

    .extra-rules-header {
      align-items: center;
      cursor: pointer;
      display: flex;
      gap: $layout-spacing;
      justify-content: space-between;
      padding: $layout-spacing $layout-spacing-lg;
      user-select: none;

      &:hover {
        background-color: darken($light-color, 2);
      }

      .extra-rules-title {
        align-items: center;
        display: flex;
        gap: $layout-spacing;
        min-width: 0;
        > svg {
          color: $primary-color;
          flex-shrink: 0;
          font-size: $font-size-lg;
        }
      }
      .extra-rules-text {
        display: flex;
        flex-direction: column;
        min-width: 0;
        strong {
          color: $gray-color-900;
        }
        small {
          color: $gray-color-dark;
          font-size: $font-size-xs;
        }
      }
      .extra-rules-right {
        align-items: center;
        display: flex;
        gap: $layout-spacing;
        .status-pill {
          margin-left: 0;
        }
      }
      .extra-rules-icon {
        color: $gray-color-dark;
        transition: transform 0.2s ease;
      }
    }

    .extra-rules-body {
      padding: 0 $layout-spacing-lg $layout-spacing-lg;
    }
  }
}
</style>
